import { useCallback, useState } from 'react';

import { makeStyles } from '@mui/styles';

import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Text,
  TextField,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';

import {
  useAlert,
  useAuth,
  useAuthFactory,
} from '@spotify/confidence-core-react';

const useStyles = makeStyles({
  overrideEnabledChip: {
    padding: 'var(--bui-space-1) var(--bui-space-3)',
    backgroundColor: 'var(--bui-bg-warning)',
    border: '1px solid var(--bui-fg-warning)',
    borderRadius: 'var(--bui-radius-full)',
  },
  rightAlignedFlexItem: {
    marginLeft: 'auto',
  },
  card: {
    maxWidth: 450,
  },
  cardFooter: {
    marginTop: 'var(--bui-space-3)',
  },
});

export const AuthDebug = () => {
  const classes = useStyles();
  const alert = useAlert();
  const auth = useAuth();
  const { overrideToken, setOverrideToken } = useAuthFactory();
  const [authToken, setAuthToken] = useState(overrideToken ?? '');

  const copyToken = useCallback(async () => {
    let token = overrideToken || auth.token;
    if (!token) {
      await auth.getAccessTokenSilently().then(fetchedToken => {
        token = fetchedToken;
      });
    }

    if (token) {
      navigator.clipboard.writeText(token);
      alert.post({
        message: 'Auth token copied to clipboard',
        severity: 'success',
        display: 'transient',
      });
    } else {
      alert.post({
        message: 'Auth token unavailable',
        severity: 'error',
        display: 'transient',
      });
    }
  }, [overrideToken, alert, auth]);

  return (
    <Flex direction="column">
      <Flex>
        <Text variant="title-x-small" weight="bold">
          Authorization
        </Text>

        {!!overrideToken && (
          <div className={classes.overrideEnabledChip} role="status">
            <Text variant="body-medium" color="warning">
              Override enabled
            </Text>
          </div>
        )}
      </Flex>

      <Card className={classes.card}>
        <CardHeader>
          <Flex align="center">
            <Text variant="body-medium" weight="bold">
              Token
            </Text>
            <Box className={classes.rightAlignedFlexItem}>
              <TooltipTrigger>
                <Button size="small" onClick={copyToken} variant="secondary">
                  Copy
                </Button>
                <Tooltip>Copy the current auth token to the clipboard.</Tooltip>
              </TooltipTrigger>
            </Box>
          </Flex>
        </CardHeader>
        <CardBody>
          <TextField
            name="auth-token"
            label="Custom auth token"
            placeholder="Enter a custom auth token"
            value={authToken}
            onChange={setAuthToken}
            autoComplete="off"
            description="Override the auth token for the current session. This will be used
              for all requests to Confidence services until the override is cleared."
          />
        </CardBody>
        <CardFooter>
          <Flex justify="end">
            <Button
              isDisabled={!overrideToken}
              onClick={() => setOverrideToken(undefined)}
              variant="tertiary"
            >
              Clear override
            </Button>
            <Button
              isDisabled={!authToken}
              onClick={() => setOverrideToken(authToken)}
              variant="secondary"
            >
              Override
            </Button>
          </Flex>
        </CardFooter>
      </Card>
    </Flex>
  );
};
